import PropTypes from 'prop-types';
import React from 'react';
import { Core } from 'reactifi';
import { sanitizeUrl } from '@braintree/sanitize-url';
import i18n from 'lib/utility/i18n';

const metaText = ({ meta, duration }) => {
  if (meta) return meta;
  if (duration) return i18n.t('%d min', { postProcess: 'sprintf', sprintf: [duration] });
  return null;
};

const NextStepCard = ({ item, variant, bg, buttonVariant, mediaPosition, onStart }) => {
  const { id, title, description, buttonLabel, imageUrl, startUrl } = item;
  const meta = metaText(item);
  const mediaLeft = mediaPosition === 'left';

  const handleStart = () => (onStart ? onStart(item) : window.location.assign(sanitizeUrl(startUrl)));

  const media = !!imageUrl && (
    <Core.Layout boxSize="14" flexShrink={0}>
      <Core.Image
        id={`next-step-image-${id}`}
        path={imageUrl}
        aspectRatio={1 / 1}
        imageProps={{ style: { objectFit: 'contain' } }}
      />
    </Core.Layout>
  );

  const startButton = (
    <Core.Button
      variant={buttonVariant}
      colorScheme="brand"
      onClick={handleStart}
      rightIcon={<Core.Icon iconName="chevron-right" prefix="far" />}
      cursor="pointer"
      aria-label={i18n.t('{{action}}: {{name}}', { action: buttonLabel || i18n.t('Start'), name: title })}
      data-action="start-next-step"
    >
      {buttonLabel || i18n.t('Start')}
    </Core.Button>
  );

  return (
    <Core.Card variant={variant} bg={bg} h="100%" data-object-type="next-step" data-object-id={id}>
      <Core.CardBody>
        <Core.Layout variant="stack" direction={{ base: 'column', sm: 'row' }} gap="md" alignItems="flex-start">
          {mediaLeft && media}
          <Core.Layout variant="stack" flex="1" gap="2xs" order={{ base: '2', sm: 'unset' }}>
            <Core.Typography id={`next-step-title-${id}`} textStyle="h4">
              {title}
            </Core.Typography>
            {!!meta && (
              <Core.Layout variant="flex" alignItems="center" gap="2xs">
                <Core.Icon iconName="clock" prefix="far" />
                <Core.Typography as="span" textStyle="p3">
                  {meta}
                </Core.Typography>
              </Core.Layout>
            )}
            {!!description && (
              <Core.Typography style={{ margin: 0 }} textStyle="p2">
                {description}
              </Core.Typography>
            )}
            {mediaLeft && (
              <Core.Layout variant="flex" justifyContent="flex-start" mt="sm">
                {startButton}
              </Core.Layout>
            )}
          </Core.Layout>
          {!mediaLeft && media}
        </Core.Layout>
      </Core.CardBody>
      {!mediaLeft && <Core.CardFooter justifyContent="flex-end">{startButton}</Core.CardFooter>}
    </Core.Card>
  );
};

NextStepCard.defaultProps = {
  variant: 'outline',
  buttonVariant: 'secondary',
  mediaPosition: 'right',
};

NextStepCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    meta: PropTypes.string,
    buttonLabel: PropTypes.string,
    imageUrl: PropTypes.string,
    startUrl: PropTypes.string.isRequired,
  }).isRequired,
  variant: PropTypes.oneOf(['elevated', 'outline', 'filled', 'unstyled']),
  bg: PropTypes.string,
  buttonVariant: PropTypes.oneOf(['primary', 'secondary', 'tertiary']),
  mediaPosition: PropTypes.oneOf(['left', 'right']),
  onStart: PropTypes.func,
};

export default NextStepCard;
